import { createFileRoute, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import Loader from "@/components/core/Loaders/Loader";
import { servicesOrchestratorValues } from "@/config/services";
import { requireAuth } from "@/lib/guards";
import { pricingSearchSchema } from "@/lib/search-params";
import { platformApi } from "@/services";
import { useStore } from "@/store";
import logger from "@/utils/logging/logger";

export const Route = createFileRoute("/$locale/$partner/pricing")({
	beforeLoad: requireAuth,
	component: QuickPricing,
	validateSearch: (search) => pricingSearchSchema.parse(search),
});

function QuickPricing() {
	const navigate = useNavigate();
	const { locale, partner } = useParams({ from: "/$locale/$partner" });
	const { manualIdentification } = useSearch({ from: "/$locale/$partner/pricing" });
	const locationId = useStore((state) => state.location?.id);
	const partnerCode = useStore((state) => state.location?.Partner?.code);
	const category = useStore((state) => state.category);
	const setAttribute = useStore((state) => state.setAttribute);
	const setDeviceInfo = useStore((state) => state.setDeviceInfo);
	const setSessionId = useStore((state) => state.setSessionId);
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		setAttribute("manualIdentification", manualIdentification);
	}, [manualIdentification, setAttribute]);

	const quickPricingUrl = useMemo(() => {
		const devicePath = manualIdentification ? `/${category.toLowerCase()}` : "";
		const extraParam = manualIdentification ? "manualIdentification=true" : "quickPricing=true";
		return `${servicesOrchestratorValues.urls.pricing}/${locale}/${partnerCode}${devicePath}?iframe=true&locationId=${locationId}&${extraParam}`;
	}, [locale, partnerCode, manualIdentification, category, locationId]);

	type PricingMessage = {
		price?: number;
		error?: string;
		sessionId?: string;
	};

	const handleMessageEvent = useCallback(
		async (event: MessageEvent<PricingMessage>) => {
			const { price, error, sessionId } = event.data;

			if (error) {
				logger.error("FLOW", "Pricing threw error", error);
				navigate({ to: "/$locale/$partner/home", params: { locale, partner }, replace: true });
			}
			if (price) {
				logger.info("INFO", "Retrieved price estimate");
				navigate({ to: "/$locale/$partner/home", params: { locale, partner }, replace: true });
			}

			if (manualIdentification && sessionId) {
				try {
					const session = await platformApi.getSessionById(sessionId);
					const rawMeta = session.Identification?.metadata;
					const device = rawMeta
						? ((typeof rawMeta === "string" ? JSON.parse(rawMeta) : rawMeta) as Record<string, unknown>)
						: null;

					setDeviceInfo({
						imei: session.Identification?.identifier ?? undefined,
						brand: device?.brand as string | undefined,
						model: device?.model as string | undefined,
						description: device?.description as string | undefined,
						capacity: device?.capacity as string | undefined,
						category,
					});

					setSessionId(sessionId);
					navigate({ to: "/$locale/$partner/conditions", params: { locale, partner }, replace: true });
				} catch (err) {
					logger.error("FLOW", "Failed to perform manual identification", err);
					navigate({ to: "/$locale/$partner/home", params: { locale, partner }, replace: true });
				}
			}
		},
		[locale, partner, navigate, manualIdentification, category, setDeviceInfo, setSessionId],
	);

	useEffect(() => {
		if (!partnerCode) {
			logger.error("FLOW", "Pricing Flow parameter is undefined or empty");
			navigate({ to: "/$locale/$partner/home", params: { locale, partner }, replace: true });
			return;
		}

		window.addEventListener("message", handleMessageEvent);

		return () => {
			window.removeEventListener("message", handleMessageEvent);
		};
	}, [handleMessageEvent, partnerCode, navigate, locale, partner]);

	return (
		<div className="h-[calc(100vh-5rem)]">
			<iframe
				id="iframe"
				title="Pricing App"
				onLoad={() => {
					setIsLoading(false);
				}}
				src={quickPricingUrl}
				height="100%"
				width="100%"
			/>
			{isLoading && (
				<div className="absolute w-fit h-fit top-1/3 left-1/2">
					<Loader />
				</div>
			)}
		</div>
	);
}
